/**
 * useAppUpdate — PWA Service Worker Update Management
 *
 * Why 'prompt' mode instead of 'autoUpdate':
 * With autoUpdate, vite-plugin-pwa calls window.location.reload() automatically
 * when a new SW activates. On iOS PWA (Add to Home Screen), this programmatic
 * reload mid-session causes the app to freeze — requiring a manual force-quit.
 *
 * With 'prompt' mode, the new SW installs and waits. This composable exposes
 * needRefresh / updateServiceWorker so the UI can show a banner and let the
 * user initiate the reload themselves (user-gesture → iOS handles it cleanly).
 *
 * Banner visibility: immer, sobald ein Update bereitliegt — in der installierten App UND
 * im normalen Browser-Tab. Bis 2026-10-21 blieb der Tab ausgenommen („user can just
 * reload"). Das stimmt nicht: in Produktion gemessen lief nach einem Neuladen weiter das
 * alte Bundle, der neue Service Worker blieb „waiting" und übernimmt erst, wenn alle Tabs
 * der App geschlossen sind. Der Klick auf den Hinweis ist eine Nutzergeste — das
 * iOS-Freeze-Problem betrifft nur das automatische Neuladen, nicht diesen Weg.
 *
 * Neuladen nach dem Klick (`applyUpdate`) macht diese App SELBST, nicht vite-plugin-pwa:
 * dessen Weg (`register.js`, Listener auf Workbox' `controlling`) lädt nur neu, wenn
 * `event.isUpdate` wahr ist, und Workbox setzt das genau einmal beim Registrieren:
 * `this._isUpdate = Boolean(navigator.serviceWorker.controller)` (Workbox.ts:119). Wurde die
 * Seite ohne Service Worker geladen — Erstbesuch, Cmd+Shift+R, „Disable cache" —, bleibt es
 * für immer falsch. Kommt danach ein Update, erscheint der Hinweis, der Klick aktiviert den
 * neuen Worker, und die Seite bleibt trotzdem auf dem alten Bundle stehen (am 31.10.2026 in
 * Chromium nachgestellt: Steuerung wechselt nach 74 ms, kein Neuladen; Matzes Meldung „hat
 * bei Klick KEINE Funktion"). Deshalb lauscht `applyUpdate` selbst auf `controllerchange`,
 * und wartet beim Klick gar kein Worker mehr (ein anderer Tab hat ihn schon aktiviert),
 * lädt es sofort neu.
 *
 * iOS cold-start & background resume:
 * iOS aggressively kills background PWA processes. What looks like a "resume"
 * is often a cold start. We handle both scenarios:
 * 1. Immediate update check when SW registration completes (cold start)
 * 2. Three resume events with a 2s network-restore delay:
 *    - visibilitychange (standard, works on most platforms)
 *    - pageshow (iOS fires this on every load — not gated by persisted flag)
 *    - focus (fallback for edge cases)
 */
import { onMounted, onUnmounted } from 'vue'
import { useRegisterSW } from 'virtual:pwa-register/vue'

export function useAppUpdate() {
  let swRegistration: ServiceWorkerRegistration | undefined
  let lastCheckAt = 0
  const CHECK_THROTTLE_MS = 10_000 // Don't check more often than every 10s

  const { needRefresh, updateServiceWorker } = useRegisterSW({
    onRegisteredSW(_swUrl, registration) {
      swRegistration = registration

      // Immediate update check on cold start (with small delay for network)
      // iOS often kills PWA processes — what looks like "resume" is a cold start.
      setTimeout(() => {
        if (registration && !registration.installing) {
          registration.update().catch(() => {})
        }
      }, 3000)

      // Poll for updates every 60 minutes when online
      setInterval(
        async () => {
          if (!registration?.installing && navigator.onLine) {
            await registration?.update()
          }
        },
        60 * 60 * 1000
      )
    },
  })

  /**
   * Trigger a SW update check with throttle.
   * No navigator.onLine guard — on iOS, onLine may still be false
   * right after resume. The update() call simply fails silently if offline.
   */
  function checkForUpdate() {
    if (!swRegistration) return
    const now = Date.now()
    if (now - lastCheckAt < CHECK_THROTTLE_MS) return
    lastCheckAt = now
    swRegistration.update().catch(() => {
      /* ignore — likely offline */
    })
  }

  /** Delayed check: gives iOS time to restore network after resume */
  function checkForUpdateDelayed() {
    setTimeout(checkForUpdate, 2000)
  }

  /**
   * Klick auf „Neu starten": neuen Service Worker aktivieren und die Seite neu laden, sobald
   * er sie übernimmt — unabhängig davon, wie die Seite ursprünglich geladen wurde.
   */
  async function applyUpdate(): Promise<void> {
    const container = navigator.serviceWorker
    const registration = swRegistration ?? (await container?.getRegistration())
    if (!container || !registration?.waiting) {
      // Kein wartender Worker mehr: das Update ist schon aktiv, nur diese Seite ist alt
      window.location.reload()
      return
    }
    container.addEventListener('controllerchange', () => window.location.reload(), {
      once: true,
    })
    await updateServiceWorker()
  }

  function handleVisibilityChange() {
    if (document.visibilityState === 'visible') {
      checkForUpdateDelayed()
    }
  }

  function handlePageShow(_event: PageTransitionEvent) {
    // Always check on pageshow — not just bfcache restores (persisted=true).
    // iOS fires pageshow on every PWA launch, including cold starts after
    // the OS killed the process. The persisted guard missed these cases.
    checkForUpdateDelayed()
  }

  function handleFocus() {
    checkForUpdateDelayed()
  }

  onMounted(() => {
    document.addEventListener('visibilitychange', handleVisibilityChange)
    window.addEventListener('pageshow', handlePageShow)
    window.addEventListener('focus', handleFocus)
  })

  onUnmounted(() => {
    document.removeEventListener('visibilitychange', handleVisibilityChange)
    window.removeEventListener('pageshow', handlePageShow)
    window.removeEventListener('focus', handleFocus)
  })

  return { needRefresh, applyUpdate }
}
